/**
 * HTTP API game-server: вход и регистрация, профиль, история, повтор, администрирование.
 * Токен сессии передаётся заголовком Authorization; ошибки сервера приходят как { message }.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AdminOverview, AdminUserRow, AuthResult, GameListItem, GameReplay, Page, ProfileDetails } from '@mt/protocol';
import { authToken } from '../net/client';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = authToken();
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: { ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('нет связи с сервером', 0);
  }
  const data = (await res.json().catch(() => null)) as (T & { message?: string }) | null;
  if (!res.ok) throw new ApiError(data?.message ?? `ошибка сервера (${res.status})`, res.status);
  return data as T;
}

export const api = {
  login: (login: string, password: string) => request<AuthResult>('POST', '/api/auth/login', { login, password }),
  register: (login: string, password: string, name: string) => request<AuthResult>('POST', '/api/auth/register', { login, password, name }),
  oauthStart: (provider: string, link = false) => request<{ url: string }>('POST', `/api/auth/oauth/${encodeURIComponent(provider)}/start`, { link }),
  me: () => request<ProfileDetails>('GET', '/api/me'),
  patchMe: (p: { name?: string; showStats?: boolean }) => request<ProfileDetails>('PATCH', '/api/me', p),
  setPassword: (p: { login?: string; current?: string; password: string }) => request<ProfileDetails>('POST', '/api/me/password', p),
  unlink: (provider: string) => request<ProfileDetails>('DELETE', `/api/me/providers/${encodeURIComponent(provider)}`),
  myGames: (offset: number, limit = 20) => request<Page<GameListItem>>('GET', `/api/me/games?offset=${offset}&limit=${limit}`),
  replay: (gameId: string) => request<GameReplay>('GET', `/api/games/${encodeURIComponent(gameId)}/replay`),
  admin: {
    overview: () => request<AdminOverview>('GET', '/api/admin/overview'),
    users: (q: string, status: string, offset: number, limit = 50) =>
      request<Page<AdminUserRow>>('GET', `/api/admin/users?q=${encodeURIComponent(q)}&status=${status}&offset=${offset}&limit=${limit}`),
    user: (id: string) => request<ProfileDetails>('GET', `/api/admin/users/${encodeURIComponent(id)}`),
    userGames: (id: string, offset: number, limit = 20) => request<Page<GameListItem>>('GET', `/api/admin/users/${encodeURIComponent(id)}/games?offset=${offset}&limit=${limit}`),
    ban: (id: string, reason: string) => request<ProfileDetails>('POST', `/api/admin/users/${encodeURIComponent(id)}/ban`, { reason }),
    unban: (id: string) => request<ProfileDetails>('POST', `/api/admin/users/${encodeURIComponent(id)}/unban`, {}),
    games: (offset: number, limit = 30) => request<Page<GameListItem>>('GET', `/api/admin/games?offset=${offset}&limit=${limit}`),
  },
};

/** Загрузка данных для экрана: { data, error, loading, reload }. Повторяется при смене deps. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): { data: T | null; error: string | null; loading: boolean; reload: () => void; set: (v: T) => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    load()
      .then((d) => alive && (setData(d), setError(null)))
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, set: setData };
}
